import React, { useEffect, useState } from "react";
import {
  Search,
  Download,
  Printer,
  FileSpreadsheet,
  FileText,
} from "lucide-react";
import { FaCheckCircle } from "react-icons/fa";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "axios";
import Apiurl from "../environment/Apiurl";

const Example = () => {
  const [activeTab, setActiveTab] = useState("medicineMaster");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [category, setCategory] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [medicineMasterData, setMedicineMasterData] = useState([]);
  const [purchaseOrderData, setPurchaseOrderData] = useState([]);
  const [purchaseListData, setPurchaseListData] = useState([]);
  const [salePharmacyData, setSalesPharmacyData] = useState([]);
  const [salelist, setSaleslist] = useState([]);
  const [stockPharmacyData, setStockPharmacyData] = useState([]);

  // Fetch data functions
  const fetchMedicineMasterData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${Apiurl}/medicine_master`);
      setMedicineMasterData(response.data);
    } catch (error) {
      console.error("Error fetching Medicine Master data:", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPurchaseOrderData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${Apiurl}/purchase_order`);
      setPurchaseOrderData(response.data);
    } catch (error) {
      console.error("Error fetching Purchase Order data:", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPurchaseListData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${Apiurl}/purchase_pharmacy_list`);
      setPurchaseListData(response.data);
    } catch (error) {
      console.error("Error fetching Purchase Pharmacy data:", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSalePharmacyData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${Apiurl}/sale`);
      setSalesPharmacyData(response.data);
    } catch (error) {
      console.error("Error fetching Sale Pharmacy data:", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSalelist = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${Apiurl}/sale_list_data`);
      setSaleslist(response.data);
    } catch (error) {
      console.error("Error fetching Sale List data:", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStockPharmacyData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${Apiurl}/stock_pharmacy`);
      setStockPharmacyData(response.data);
    } catch (error) {
      console.error("Error fetching Stock Pharmacy data:", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    fetchMedicineMasterData();
    fetchPurchaseOrderData();
    fetchPurchaseListData();
    fetchSalePharmacyData();
    fetchSalelist();
    fetchStockPharmacyData();
  }, []);

  // Tab configurations
  const tabConfig = {
    medicineMaster: {
      title: "Medicine Master",
      headers: ["S.No", "Medicine", "Category", "Medicine Type", "Manufacturer", "HSN Code"],
      fields: ["medicine_name", "category", "medicine_type", "manufacturer", "hsn_code"],
      data: medicineMasterData,
    },
    purchaseOrder: {
      title: "Purchase Order",
      headers: ["S.No", "Date", "Pur_Order No", "Supplier Name", "Order Status"],
      fields: ["date", "purchase_order_no", "supplier_details[0].supplier_name", "order_status"],
      data: purchaseOrderData,
    },
    purchasePharmacy: {
      title: "Purchase Pharmacy",
      headers: ["S.No", "Purchase Date", "Invoice No",  "Supplier Name", "Medicine Name","HSN code","Batch No", "Unit Pack", "Quantity", "MRP", "Amount (₹)"],
      fields: ["purchase_date", "invoice_number",  "supplier_id","medicine_name","hsn_code", "batch_no", "unit_pack", "quantity", "mrp", "net_total"],
      data: purchaseListData,
    },
    salePharmacy: {
      title: "Sale Pharmacy",
      headers: ["S.No", "Sale Date", "Invoice No", "Patient ID", "Patient Name", "Category", "Pur Price", "MRP", "Qty", "Net Total", "Paid", "PayType", "Profit"],
      fields: [
        "sale_date",
        "invoice_number",
        "patient_id",
        "patient_details[0].first_name",
        "patient_details[0].patient_type",
        "sales_list[0].purchase_price_per_unit",
        "sales_list[0].mrp",
        "sales_list[0].quantity",
        "net_total",
        "paid",
        "sales_pharmacy_payment[0].pay_type",
        "profit",
      ],
      data: salePharmacyData,
    },
    salelist: {
      title: "Product List",
      headers: [
        "S.No",
        "Sale Date",
        "Invoice No",
        "Patient ID",
        "Patient Name",
        "Medicine Name",
        "Hsn Code",
        "Category",
        "MRP",
        "Qty",
        "CGST", 
        "SGST",
        "Tax Value",
        "Net Total",
      ],
      fields: [
        "sale_date",
        "invoice_number",
        "patient_id",
        "patient_name",
        "medicine_name",
        "hsn_code",
        "category",
        "mrp",
        "qty",
        "cgst",
        "sgst",
        "tax_row_value",
        "net_total",
      ],
      data: salelist,
    },
    stockManagement: {
      title: "Stock Management",
      headers: ["S.No", "Medicine", "Category", "Batch No", "Exp Date", "InQty", "OutQty", "Balance", "Pur Price", "MRP", "Profit"],
      fields: [
        "medicine_details.medicine_name",
        "medicine_details.category",
        "batch_no",
        "expiry_date",
        "inward_quantity",
        "outward_quantity",
        "balance_quantity",
        "purchase_price",
        "mrp",
        "profit",
      ],
      data: stockPharmacyData,
    },
  };

  // Calculate per tablet price, per tablet MRP, and profit for stock items
  const calculateStockValues = (item) => {
    const purchasePrice = parseFloat(item.purchase_price) || 0;
    const mrp = parseFloat(item.mrp) || 0;
    let tabletsPerPack = 1;

    if (typeof item.unit_pack === 'string' && item.unit_pack.includes("*")) {
      const parts = item.unit_pack.split("*");
      if (parts.length === 2) {
        tabletsPerPack = parseInt(parts[1]) || 1;
      }
    }

    const perTabletPrice = tabletsPerPack > 0 ? purchasePrice / tabletsPerPack : 0;
    const perTabletMrp = tabletsPerPack > 0 ? mrp / tabletsPerPack : 0;
    const profit = parseFloat(item.profit) || 0;

    return {
      perTabletPrice: perTabletPrice.toFixed(2),
      perTabletMrp: perTabletMrp.toFixed(2),
      profit: profit.toFixed(2),
    };
  };

  // Get nested field value
  const getNestedValue = (obj, path) => {
    // Handle profit for salePharmacy
    if (path === "profit" && activeTab === "salePharmacy") {
      if (!obj.sales_list || !Array.isArray(obj.sales_list)) {
        console.warn(`Missing or invalid sales_list for invoice ${obj.invoice_number}`);
        return "0.00";
      }
      return obj.sales_list
        .reduce((sum, sl) => sum + (parseFloat(sl.profit) || 0), 0)
        .toFixed(2);
    }

    // Handle sales_pharmacy_payment
    if (path === "sales_pharmacy_payment[0].pay_type") {
      const payments = Array.isArray(obj.sales_pharmacy_payment) ? obj.sales_pharmacy_payment : [];
      if (paymentMode) {
        const filteredPayments = payments.filter((p) => p.pay_type === paymentMode);
        return filteredPayments.length > 0 ? paymentMode : "N/A";
      }
      return payments.map((p) => p.pay_type).join(", ") || "N/A";
    }

    // Handle stockManagement profit
    if (activeTab === "stockManagement" && path === "profit") {
      return calculateStockValues(obj).profit;
    }

    // Handle numeric fields for salelist
    if (
      activeTab === "salelist" &&
      [
        "pur_price",
        "mrp",
        "qty",
        "cgst",
        "sgst",
        "tax_row_value",
        "discount_value",
        "net_total",
      ].includes(path)
    ) {
      return isNaN(obj[path]) || obj[path] === null || obj[path] === undefined
        ? "0.00"
        : Number(obj[path]).toFixed(2);
    }

    // Handle numeric fields for purchasePharmacy
    if (activeTab === "purchasePharmacy") {
      if (path === "quantity") {
        // Return quantity as integer
        const value = parseInt(obj[path]);
        return isNaN(value) || obj[path] === null || obj[path] === undefined
          ? "0"
          : value;
      }
      if (["mrp", "net_total"].includes(path)) {
        return isNaN(obj[path]) || obj[path] === null || obj[path] === undefined
          ? "0.00"
          : Number(obj[path]).toFixed(2);
      }
    }

    // General nested value extraction
    const parts = path.match(/(\w+|\[\d+\])/g) || [path];
    let current = obj;

    for (let part of parts) {
      if (part.startsWith("[") && part.endsWith("]")) {
        const index = parseInt(part.slice(1, -1));
        current = current ? current[index] : undefined;
      } else {
        current = current ? current[part] : undefined;
      }
      if (!current) break;
    }

    if (
      ["sub_total", "net_total", "discount_value", "paid", "purchase_price", "mrp"].includes(
        path.split(".").pop()
      )
    ) {
      return isNaN(current) || current === null || current === undefined
        ? "0.00"
        : Number(current).toFixed(2);
    }

    return current || "N/A";
  };

  // Get filtered data based on tab and filters
  const getCurrentData = () => {
    const config = tabConfig[activeTab];
    if (!config) return [];

    let filteredData = config.data;

    // Apply category filter for Medicine Master
    if (activeTab === "medicineMaster" && category) {
      filteredData = filteredData.filter((item) => item.category === category);
    }

    // Apply category filter for Stock Management
    if (activeTab === "stockManagement" && category) {
      filteredData = filteredData.filter((item) => item.medicine_details?.category === category);
    }

    // Apply category and date filters for Sale List
    if (activeTab === "salelist") {
      filteredData = filteredData.filter((item) => {
        let dateMatch = true;
        let categoryMatch = true;

        if (fromDate || toDate) {
          const saleDate = new Date(item.sale_date);
          if (isNaN(saleDate.getTime())) {
            console.warn(`Invalid sale_date for invoice ${item.invoice_number}`);
            return false;
          }

          if (fromDate) {
            const from = new Date(fromDate);
            from.setHours(0, 0, 0, 0);
            saleDate.setHours(0, 0, 0, 0);
            if (saleDate < from) {
              dateMatch = false;
            }
          }

          if (toDate && dateMatch) {
            const to = new Date(toDate);
            to.setHours(23, 59, 59, 999);
            const saleDateEnd = new Date(item.sale_date);
            saleDateEnd.setHours(23, 59, 59, 999);
            if (saleDateEnd > to) {
              dateMatch = false;
            }
          }
        }

        if (category) {
          categoryMatch = item.category === category;
        }

        return dateMatch && categoryMatch;
      });
    }

    // Apply date, payment mode, and category filters for Sale Pharmacy
    if (activeTab === "salePharmacy") {
      filteredData = filteredData.filter((item) => {
        let dateMatch = true;

        if (fromDate || toDate) {
          const saleDate = new Date(item.sale_date);
          if (isNaN(saleDate.getTime())) {
            console.warn(`Invalid sale_date for invoice ${item.invoice_number}`);
            return false;
          }

          if (fromDate) {
            const from = new Date(fromDate);
            from.setHours(0, 0, 0, 0);
            saleDate.setHours(0, 0, 0, 0);
            if (saleDate < from) {
              dateMatch = false;
            }
          }

          if (toDate && dateMatch) {
            const to = new Date(toDate);
            to.setHours(23, 59, 59, 999);
            const saleDateEnd = new Date(item.sale_date);
            saleDateEnd.setHours(23, 59, 59, 999);
            if (saleDateEnd > to) {
              dateMatch = false;
            }
          }
        }

        let paymentMatch = true;
        if (paymentMode) {
          const payments = Array.isArray(item.sales_pharmacy_payment) ? item.sales_pharmacy_payment : [];
          paymentMatch = payments.some((p) => p.pay_type === paymentMode);
        }

        let categoryMatch = true;
        if (category) {
          const patientType = item.patient_details && item.patient_details[0] ? item.patient_details[0].patient_type : null;
          categoryMatch = patientType === category;
        }

        return dateMatch && paymentMatch && categoryMatch;
      });
    }

    // Apply date filtering for Purchase Order
    if (activeTab === "purchaseOrder" && (fromDate || toDate)) {
      filteredData = filteredData.filter((item) => {
        let dateMatch = true;

        if (fromDate || toDate) {
          const itemDate = new Date(item.date);
          if (isNaN(itemDate.getTime())) {
            console.warn(`Invalid date for purchase order ${item.purchase_order_no}`);
            return false;
          }

          if (fromDate) {
            const from = new Date(fromDate);
            from.setHours(0, 0, 0, 0);
            itemDate.setHours(0, 0, 0, 0);
            if (itemDate < from) {
              dateMatch = false;
            }
          }

          if (toDate && dateMatch) {
            const to = new Date(toDate);
            to.setHours(23, 59, 59, 999);
            const itemDateEnd = new Date(item.date);
            itemDateEnd.setHours(23, 59, 59, 999);
            if (itemDateEnd > to) {
              dateMatch = false;
            }
          }
        }

        return dateMatch;
      });
    }

    // Apply date filtering for Purchase Pharmacy
    if (activeTab === "purchasePharmacy" && (fromDate || toDate)) {
      filteredData = filteredData.filter((item) => {
        let dateMatch = true;

        if (fromDate || toDate) {
          const itemDate = new Date(item.purchase_date);
          if (isNaN(itemDate.getTime())) {
            console.warn(`Invalid purchase_date for invoice ${item.invoice_number}`);
            return false;
          }

          if (fromDate) {
            const from = new Date(fromDate);
            from.setHours(0, 0, 0, 0);
            itemDate.setHours(0, 0, 0, 0);
            if (itemDate < from) {
              dateMatch = false;
            }
          }

          if (toDate && dateMatch) {
            const to = new Date(toDate);
            to.setHours(23, 59, 59, 999);
            const itemDateEnd = new Date(item.purchase_date);
            itemDateEnd.setHours(23, 59, 59, 999);
            if (itemDateEnd > to) {
              dateMatch = false;
            }
          }
        }

        return dateMatch;
      });
    }

    return filteredData.map((item, index) => {
      const row = { sNo: index + 1 };
      config.fields.forEach((field) => {
        if (field === "paid" && activeTab === "salePharmacy" && paymentMode) {
          const payments = Array.isArray(item.sales_pharmacy_payment) ? item.sales_pharmacy_payment : [];
          const paymentAmount = payments
            .filter((p) => p.pay_type === paymentMode)
            .reduce((sum, p) => sum + (parseFloat(p.paid_amount) || 0), 0);
          row[field] = paymentAmount > 0 ? paymentAmount.toFixed(2) : "0.00";
        } else {
          row[field] = getNestedValue(item, field);
        }
      });
      return row;
    });
  };

  // Handle tab change
  const handleTabChange = (key) => {
    setActiveTab(key);
    setCurrentPage(1);
    setSearchTerm("");
    setFromDate("");
    setToDate("");
    setPaymentMode("");
    setCategory("");
  };

  // Filter data based on search term
  const getFilteredData = () => {
    const currentData = getCurrentData();
    if (!searchTerm) return currentData;

    return currentData.filter((item) =>
      Object.values(item).some((value) =>
        value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  };

  // Get paginated data
  const getPaginatedData = () => {
    const filteredData = getFilteredData();
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  };

  // Calculate total pages
  const getTotalPages = () => {
    return Math.ceil(getFilteredData().length / itemsPerPage);
  };

  // Calculate totals for Sale Pharmacy
  const getSalePharmacyTotals = () => {
    const filteredData = getFilteredData();
    if (activeTab !== "salePharmacy") return null;

    return filteredData.reduce(
      (acc, item) => {
        const netTotal = parseFloat(item.net_total) || 0;
        let paidAmount = 0;
        const profit = parseFloat(item.profit) || 0;

        if (paymentMode) {
          const originalItem = salePharmacyData.find(
            (orig) => orig.sale_date === item.sale_date && orig.invoice_number === item.invoice_number
          );
          if (originalItem) {
            const payments = Array.isArray(originalItem.sales_pharmacy_payment)
              ? originalItem.sales_pharmacy_payment
              : [];
            paidAmount = payments
              .filter((p) => p.pay_type === paymentMode)
              .reduce((sum, p) => sum + (parseFloat(p.paid_amount) || 0), 0);
          }
        } else {
          paidAmount = parseFloat(item.paid) || 0;
        }

        return {
          netTotal: acc.netTotal + netTotal,
          paid: acc.paid + paidAmount,
          profit: acc.profit + profit,
        };
      },
      { netTotal: 0, paid: 0, profit: 0 }
    );
  };

  // Calculate totals for Sale List
  const getSaleListTotals = () => {
    const filteredData = getFilteredData();
    if (activeTab !== "salelist") return null;

    return filteredData.reduce(
      (acc, item) => ({
        pur_price: acc.pur_price + (parseFloat(item.pur_price) || 0),
        mrp: acc.mrp + (parseFloat(item.mrp) || 0),
        qty: acc.qty + (parseFloat(item.qty) || 0),
        cgst: acc.cgst + (parseFloat(item.cgst) || 0),
        sgst: acc.sgst + (parseFloat(item.sgst) || 0),
        tax_row_value: acc.tax_row_value + (parseFloat(item.tax_row_value) || 0),
        discount_value: acc.discount_value + (parseFloat(item.discount_value) || 0),
        net_total: acc.net_total + (parseFloat(item.net_total) || 0),
      }),
      {
        pur_price: 0,
        mrp: 0,
        qty: 0,
        cgst: 0,
        sgst: 0,
        tax_row_value: 0,
        discount_value: 0,
        net_total: 0,
      }
    );
  };

  // Calculate totals for Stock Management
  const getStockManagementTotals = () => {
    const filteredData = getFilteredData();
    if (activeTab !== "stockManagement") return null;

    return filteredData.reduce(
      (acc, item) => {
        const purchasePrice = parseFloat(item.purchase_price) || 0;
        const mrp = parseFloat(item.mrp) || 0;
        const profit = parseFloat(item.profit) || 0;

        return {
          totalPurchasePrice: acc.totalPurchasePrice + purchasePrice,
          totalMrp: acc.totalMrp + mrp,
          totalProfit: acc.totalProfit + profit,
        };
      },
      { totalPurchasePrice: 0, totalMrp: 0, totalProfit: 0 }
    );
  };

  // Calculate totals for Purchase Pharmacy
  const getPurchasePharmacyTotals = () => {
    const filteredData = getFilteredData();
    if (activeTab !== "purchasePharmacy") return null;

    return filteredData.reduce(
      (acc, item) => ({
        quantity: acc.quantity + (parseInt(item.quantity) || 0),
        mrp: acc.mrp + (parseFloat(item.mrp) || 0),
        net_total: acc.net_total + (parseFloat(item.net_total) || 0),
      }),
      {
        quantity: 0,
        mrp: 0,
        net_total: 0,
      }
    );
  };

  // Export functions
  const downloadCSV = () => {
    const currentData = getFilteredData();
    const config = tabConfig[activeTab];
    let headers = config.headers;
    let fields = ["sNo", ...config.fields];

    let csvContent = headers.join(",") + "\n";
    currentData.forEach((item) => {
      const row = fields.map((field) => `"${item[field] || ""}"`).join(",");
      csvContent += row + "\n";
    });

    if (activeTab === "salePharmacy") {
      const totals = getSalePharmacyTotals();
      const totalsRow = [
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        totals.netTotal.toFixed(2),
        totals.paid.toFixed(2),
        "",
        totals.profit.toFixed(2),
      ];
      csvContent += totalsRow.join(",") + "\n";
    }

    if (activeTab === "salelist") {
      const totals = getSaleListTotals();
      const totalsRow = [
        "",
        "",
        "",
        "",
        "",
        "",
        totals.pur_price.toFixed(2),
        totals.mrp.toFixed(2),
        totals.qty.toFixed(2),
        totals.cgst.toFixed(2),
        totals.sgst.toFixed(2),
        totals.tax_row_value.toFixed(2),
        totals.discount_value.toFixed(2),
        totals.net_total.toFixed(2),
      ];
      csvContent += totalsRow.join(",") + "\n";
    }

    if (activeTab === "stockManagement") {
      const totals = getStockManagementTotals();
      const totalsRow = [
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        totals.totalPurchasePrice.toFixed(2),
        totals.totalMrp.toFixed(2),
        totals.totalProfit.toFixed(2),
      ];
      csvContent += totalsRow.join(",") + "\n";
    }

    if (activeTab === "purchasePharmacy") {
      const totals = getPurchasePharmacyTotals();
      const totalsRow = [
        "",
        "",
        "",
        "",
        "",
        "",
        totals.quantity,
        totals.mrp.toFixed(2),
        totals.net_total.toFixed(2),
      ];
      csvContent += totalsRow.join(",") + "\n";
    }

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${activeTab}_report.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadExcel = () => {
    const currentData = getFilteredData();
    const config = tabConfig[activeTab];
    let headers = config.headers;
    let fields = ["sNo", ...config.fields];

    let excelContent = '<table border="1">';
    excelContent += "<tr>" + headers.map((h) => `<th>${h}</th>`).join("") + "</tr>";

    currentData.forEach((item) => {
      excelContent += "<tr>" + fields.map((field) => `<td>${item[field] || ""}</td>`).join("") + "</tr>";
    });

    if (activeTab === "salePharmacy") {
      const totals = getSalePharmacyTotals();
      excelContent +=
        "<tr>" +
        [
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          totals.netTotal.toFixed(2),
          totals.paid.toFixed(2),
          "",
          totals.profit.toFixed(2),
        ]
          .map((val) => `<td>${val}</td>`)
          .join("") +
        "</tr>";
    }

    if (activeTab === "salelist") {
      const totals = getSaleListTotals();
      excelContent +=
        "<tr>" +
        [
          "",
          "",
          "",
          "",
          "",
          "",
          totals.pur_price.toFixed(2),
          totals.mrp.toFixed(2),
          totals.qty.toFixed(2),
          totals.cgst.toFixed(2),
          totals.sgst.toFixed(2),
          totals.tax_row_value.toFixed(2),
          totals.discount_value.toFixed(2),
          totals.net_total.toFixed(2),
        ]
          .map((val) => `<td>${val}</td>`)
          .join("") +
        "</tr>";
    }

    if (activeTab === "stockManagement") {
      const totals = getStockManagementTotals();
      excelContent +=
        "<tr>" +
        [
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          totals.totalPurchasePrice.toFixed(2),
          totals.totalMrp.toFixed(2),
          totals.totalProfit.toFixed(2),
        ]
          .map((val) => `<td>${val}</td>`)
          .join("") +
        "</tr>";
    }

    if (activeTab === "purchasePharmacy") {
      const totals = getPurchasePharmacyTotals();
      excelContent +=
        "<tr>" +
        [
          "",
          "",
          "",
          "",
          "",
          "",
          totals.quantity,
          totals.mrp.toFixed(2),
          totals.net_total.toFixed(2),
        ]
          .map((val) => `<td>${val}</td>`)
          .join("") +
        "</tr>";
    }

    excelContent += "</table>";

    const blob = new Blob([excelContent], { type: "application/vnd.ms-excel" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${activeTab}_report.xls`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadPDF = () => {
    const currentData = getFilteredData();
    const config = tabConfig[activeTab];
    let headers = config.headers;
    let fields = ["sNo", ...config.fields];

    const doc = new jsPDF();

    doc.setFontSize(18);
    doc.text(`${config.title} Report`, 14, 20);

    doc.setFontSize(12);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 30);

    const tableData = currentData.map((item) => fields.map((field) => item[field] || ""));

    if (activeTab === "salePharmacy") {
      const totals = getSalePharmacyTotals();
      tableData.push([
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        totals.netTotal.toFixed(2),
        totals.paid.toFixed(2),
        "",
        totals.profit.toFixed(2),
      ]);
    }

    if (activeTab === "salelist") {
      const totals = getSaleListTotals();
      tableData.push([
        "",
        "",
        "",
        "",
        "",
        "",
        totals.pur_price.toFixed(2),
        totals.mrp.toFixed(2),
        totals.qty.toFixed(2),
        totals.cgst.toFixed(2),
        totals.sgst.toFixed(2),
        totals.tax_row_value.toFixed(2),
        totals.discount_value.toFixed(2),
        totals.net_total.toFixed(2),
      ]);
    }

    if (activeTab === "stockManagement") {
      const totals = getStockManagementTotals();
      tableData.push([
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        totals.totalPurchasePrice.toFixed(2),
        totals.totalMrp.toFixed(2),
        totals.totalProfit.toFixed(2),
      ]);
    }

    if (activeTab === "purchasePharmacy") {
      const totals = getPurchasePharmacyTotals();
      tableData.push([
        "",
        "",
        "",
        "",
        "",
        "",
        totals.quantity,
        totals.mrp.toFixed(2),
        totals.net_total.toFixed(2),
      ]);
    }

    autoTable(doc, {
      startY: 40,
      head: [headers],
      body: tableData,
      theme: "striped",
      headStyles: {
        fillColor: [46, 146, 144],
        textColor: [255, 255, 255],
        fontSize: 10,
      },
      bodyStyles: {
        fontSize: 9,
      },
      alternateRowStyles: {
        fillColor: [242, 242, 242],
      },
      margin: { top: 40 },
    });

    doc.save(`${activeTab}_report.pdf`);
  };

  const handlePrint = () => {
    const currentData = getFilteredData();
    const config = tabConfig[activeTab];
    let headers = config.headers;
    let fields = ["sNo", ...config.fields];

    const printWindow = window.open("", "_blank");
    let htmlContent = `
      <html>
        <head>
          <title>${config.title} Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #01AEBE; text-align: center; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #01AEBE; color: white; }
            tr:nth-child(even) { background-color: #f2f2f2; }
            @media print {
              body { margin: 0; }
              h1 { margin-bottom: 20px; }
            }
          </style>
        </head>
        <body>
          <h1>${config.title} Report</h1>
          <p>Generated on: ${new Date().toLocaleDateString()}</p>
          <table>
            <thead>
              <tr>${headers.map((h) => `<th>${h}</th>`).join("")}</tr>
            </thead>
            <tbody>
              ${currentData
                .map(
                  (item) =>
                    `<tr>${fields.map((field) => `<td>${item[field] || ""}</td>`).join("")}</tr>`
                )
                .join("")}
              ${activeTab === "salePharmacy"
                ? `<tr>${[
                    "",
                    "",
                    "",
                    "",
                    "",
                    "",
                    "",
                    "",
                    "",
                    getSalePharmacyTotals().netTotal.toFixed(2),
                    getSalePharmacyTotals().paid.toFixed(2),
                    "",
                    getSalePharmacyTotals().profit.toFixed(2),
                  ]
                    .map((val) => `<td><strong>${val}</strong></td>`)
                    .join("")}</tr>`
                : ""
              }
              ${activeTab === "salelist"
                ? `<tr>${[
                    "",
                    "",
                    "",
                    "",
                    "",
                    "",
                    getSaleListTotals().pur_price.toFixed(2),
                    getSaleListTotals().mrp.toFixed(2),
                    getSaleListTotals().qty.toFixed(2),
                    getSaleListTotals().cgst.toFixed(2),
                    getSaleListTotals().sgst.toFixed(2),
                    getSaleListTotals().tax_row_value.toFixed(2),
                    getSaleListTotals().discount_value.toFixed(2),
                    getSaleListTotals().net_total.toFixed(2),
                  ]
                    .map((val) => `<td><strong>${val}</strong></td>`)
                    .join("")}</tr>`
                : ""
              }
              ${activeTab === "stockManagement"
                ? `<tr>${[
                    "",
                    "",
                    "",
                    "",
                    "",
                    "",
                    "",
                    "",
                    getStockManagementTotals().totalPurchasePrice.toFixed(2),
                    getStockManagementTotals().totalMrp.toFixed(2),
                    getStockManagementTotals().totalProfit.toFixed(2),
                  ]
                    .map((val) => `<td><strong>${val}</strong></td>`)
                    .join("")}</tr>`
                : ""
              }
              ${activeTab === "purchasePharmacy"
                ? `<tr>${[
                    "",
                    "",
                    "",
                    "",
                    "",
                    "",
                    getPurchasePharmacyTotals().quantity,
                    getPurchasePharmacyTotals().mrp.toFixed(2),
                    getPurchasePharmacyTotals().net_total.toFixed(2),
                  ]
                    .map((val) => `<td><strong>${val}</strong></td>`)
                    .join("")}</tr>`
                : ""
              }
            </tbody>
          </table>
        </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  // Render table
  const renderTable = () => {
    const paginatedData = getPaginatedData();
    const config = tabConfig[activeTab];
    const fields = ["sNo", ...config.fields];

    return (
      <div className="table-responsive">
        <table className="table table-bordered">
          <thead>
            <tr style={{ backgroundColor: "#01AEBE", color: "white" }}>
              {config.headers.map((header, index) => (
                <th key={index} className="border-0">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((item, index) => (
              <tr key={index} className="align-middle">
                {fields.map((field, fieldIndex) => (
                  <td key={fieldIndex} className="border-0">
                    {field === "order_status" ? (
                      <span
                        className={`d-inline-flex align-items-center gap-1 badge rounded-pill bg-success-subtle text-success fw-semibold px-3 py-2 ${getStatusBadgeClass(
                          item[field]
                        )}`}
                        style={{ borderRadius: "1rem" }}
                      >
                        <FaCheckCircle />
                        {item[field]}
                      </span>
                    ) : (
                      item[field]
                    )}
                  </td>
                ))}
              </tr>
            ))}
            {activeTab === "salePharmacy" && (
              <tr style={{ backgroundColor: "#f2f2f2", fontWeight: "bold" }}>
                <td colSpan={9}>Totals</td>
                <td>{getSalePharmacyTotals()?.netTotal.toFixed(2)}</td>
                <td>{getSalePharmacyTotals()?.paid.toFixed(2)}</td>
                <td></td>
                <td>{getSalePharmacyTotals()?.profit.toFixed(2)}</td>
              </tr>
            )}
            {activeTab === "salelist" && (
              <tr style={{ backgroundColor: "#f2f2f2", fontWeight: "bold" }}>
                <td colSpan={8}>Totals</td>
                <td>{getSaleListTotals()?.mrp.toFixed(2)}</td>
                <td>{getSaleListTotals()?.qty.toFixed(2)}</td>
                <td>{getSaleListTotals()?.cgst.toFixed(2)}</td>
                <td>{getSaleListTotals()?.sgst.toFixed(2)}</td>
                <td>{getSaleListTotals()?.tax_row_value.toFixed(2)}</td>
                <td>{getSaleListTotals()?.net_total.toFixed(2)}</td>
              </tr>
            )}
            {activeTab === "stockManagement" && (
              <tr style={{ backgroundColor: "#f2f2f2", fontWeight: "bold" }}>
                <td colSpan={8}>Totals</td>
                <td>{getStockManagementTotals()?.totalPurchasePrice.toFixed(2)}</td>
                <td>{getStockManagementTotals()?.totalMrp.toFixed(2)}</td>
                <td>{getStockManagementTotals()?.totalProfit.toFixed(2)}</td>
              </tr>
            )}
            {activeTab === "purchasePharmacy" && (
              <tr style={{ backgroundColor: "#f2f2f2", fontWeight: "bold" }}>
                <td colSpan={8}>Totals</td>
                <td>{getPurchasePharmacyTotals()?.quantity}</td>
                <td>{getPurchasePharmacyTotals()?.mrp.toFixed(2)}</td>
                <td>{getPurchasePharmacyTotals()?.net_total.toFixed(2)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      Active: "bg-success",
      Available: "bg-success",
      Paid: "bg-success",
      Completed: "bg-success",
      Pending: "bg-warning",
      "Low Stock": "bg-warning",
      "Out of Stock": "bg-danger",
    };
    return statusClasses[status] || "bg-success";
  };

  // Render pagination
  const renderPagination = () => {
    const totalPages = getTotalPages();
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="text-muted">
          Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
          {Math.min(currentPage * itemsPerPage, getFilteredData().length)} of{" "}
          {getFilteredData().length} entries
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          {pages.map((page) => (
            <button
              key={page}
              className={`btn btn-sm ${currentPage === page ? "btn-success" : "btn-outline-secondary"}`}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </button>
          ))}
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="container-fluid p-4" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      <div className="mb-4">
        <ul className="nav nav-tabs flex-wrap">
          {Object.entries(tabConfig).map(([key, config]) => (
            <li key={key} className="nav-item">
              <button
                className={`nav-link ${activeTab === key ? "active" : ""} px-3 py-2`}
                style={{
                  backgroundColor: activeTab === key ? "#01AEBE" : "transparent",
                  color: activeTab === key ? "white" : "#01AEBE",
                  border: "1px solid #01AEBE",
                  borderRadius: "0",
                  fontSize: "14px",
                }}
                onClick={() => handleTabChange(key)}
              >
                {config.title}
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className="card shadow-sm border-0">
        <div className="card-header" style={{ backgroundColor: "#01AEBE", color: "white" }}>
          <div className="row d-flex justify-content-between align-items-center">
            <div className="col-lg-5 col-md-5 d-flex align-items-center">
              <h5 className="mb-0 fw-bold">{tabConfig[activeTab]?.title}</h5>
            </div>

            <div className="col-lg-7 col-md-7 d-flex justify-content-center align-items-start gap-2 flex-wrap">
              {["salePharmacy", "purchaseOrder", "purchasePharmacy", "salelist"].includes(activeTab) && (
                <>
                  <div>
                    <label className="form-label small mb-1 text-white">From Date:</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={fromDate}
                      onChange={(e) => {
                        setFromDate(e.target.value);
                        setCurrentPage(1);
                      }}
                      max={toDate || undefined}
                    />
                  </div>

                  <div>
                    <label className="form-label small mb-1 text-white">To Date:</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={toDate}
                      onChange={(e) => {
                        setToDate(e.target.value);
                        setCurrentPage(1);
                      }}
                      min={fromDate || undefined}
                    />
                  </div>

                  {(fromDate || toDate) && (
                    <div>
                      <label className="form-label small mb-1 text-white">&nbsp;</label>
                      <button
                        className="btn btn-outline-light btn-sm w-100"
                        onClick={() => {
                          setFromDate("");
                          setToDate("");
                          setCurrentPage(1);
                        }}
                      >
                        Clear Dates
                      </button>
                    </div>
                  )}
                </>
              )}

              {activeTab === "salePharmacy" && (
                <>
                  <div>
                    <label className="form-label small mb-1 text-white">Paytype:</label>
                    <select
                      className="form-select form-select-sm"
                      value={paymentMode}
                      onChange={(e) => {
                        setPaymentMode(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <option value="">All</option>
                      <option value="Cash">Cash</option>
                      <option value="Card">Card</option>
                      <option value="Gpay">Gpay</option>
                      <option value="Phonepe">Phonepe</option>
                      <option value="Paytm">Paytm</option>
                      <option value="Amazon Pay">Amazon Pay</option>
                      <option value="UPI">UPI</option>
                    </select>
                  </div>

                  <div>
                    <label className="form-label small mb-1 text-white">Category:</label>
                    <select
                      className="form-select form-select-sm"
                      value={category}
                      onChange={(e) => {
                        setCategory(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <option value="">All</option>
                      <option value="Homeopathy">Homeopathy</option>
                      <option value="Yoga">Yoga</option>
                    </select>
                  </div>
                </>
              )}

              {(activeTab === "medicineMaster" || activeTab === "stockManagement" || activeTab === "salelist") && (
                <>
                  <div>
                    <label className="form-label small mb-1 text-white">Category:</label>
                    <select
                      className="form-select form-select-sm"
                      value={category}
                      onChange={(e) => {
                        setCategory(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <option value="">All</option>
                      <option value="Homeopathy">Homeopathy</option>
                      <option value="Yoga">Yoga</option>
                    </select>
                  </div>
                </>
              )}
            </div>
          </div>

          {fromDate && toDate && new Date(fromDate) > new Date(toDate) && (
            <div className="text-danger small mt-1 text-end">From Date cannot be later than To Date</div>
          )}
        </div>

        <div className="card-body">
          <div className="row mb-3 align-items-center crm-table-toolbar">
            <div className="col-md-3">
              <div className="input-group input-group-sm w-100">
                <span className="input-group-text">
                  <i className="fas fa-table"></i>
                </span>
                <select
                  className="form-select"
                  value={itemsPerPage}
                  onChange={(e) => {
                    const value = e.target.value === "All" ? getFilteredData().length : Number(e.target.value);
                    setItemsPerPage(value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="All">Show All</option>
                  <option value="5">Show 5</option>
                  <option value="10">Show 10</option>
                  <option value="20">Show 20</option>
                  <option value="50">Show 50</option>
                  <option value="100">Show 100</option>
                  <option value="200">Show 200</option>
                  <option value="500">Show 500</option>
                  <option value="1000">Show 1000</option>
                </select>
              </div>
            </div>

            <div className="col-md-6">
              <div className="d-flex flex-wrap gap-2">
                <button
                  className="btn btn-success btn-sm d-flex align-items-center gap-1"
                  onClick={downloadPDF}
                >
                  <FileText size={16} /> PDF
                </button>
                <button
                  className="btn btn-success btn-sm d-flex align-items-center gap-1"
                  onClick={downloadExcel}
                >
                  <FileSpreadsheet size={16} /> Excel
                </button>
                <button
                  className="btn btn-success btn-sm d-flex align-items-center gap-1"
                  onClick={downloadCSV}
                >
                  <Download size={16} /> CSV
                </button>
                <button
                  className="btn btn-success btn-sm d-flex align-items-center gap-1"
                  onClick={handlePrint}
                >
                  <Printer size={16} /> Print
                </button>
              </div>
            </div>

            <div className="col-md-3">
              <div className="position-relative">
                <input
                  type="text"
                  className="form-control form-control-sm pe-5"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
                <Search
                  size={16}
                  className="position-absolute text-muted"
                  style={{ right: "10px", top: "50%", transform: "translateY(-50%)" }}
                />
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="text-center">
              <div className="spinner-border text-success" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              {renderTable()}
              {renderPagination()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Example;